/**
 * Screen-local fetch module for the partner-endpoints screen (epic 047
 * task 05; ADR-016 §7). Mirrors the partner-credentials/api.ts pattern:
 * local `request()` + `ApiError`; `lib/api.ts` is never edited by a
 * Stage-C screen (ADR-010 A2 b). Wire types come from
 * `@leads-platform/shared`; no local type mirror.
 */

import type {
  CreatePartnerEndpoint,
  EndpointTransport,
  EndpointVariable,
  PartnerCredentialRowResponse,
  PartnerEndpointResponse,
  TestRunSummary,
} from '@leads-platform/shared';
import { api, apiFetch, type CampaignDetailResponse } from '../../lib/api';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';
const ADMIN_PREFIX = '/admin';

/**
 * Error twin of `lib/api.ts`'s `ApiError` — same `{ status, message,
 * validationErrors }` shape, so screens catch this module's errors exactly as
 * they catch the shared client's.
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public validationErrors?: Record<string, string[]>
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Map a backend validation error array (`{path, message, code}`) into a
 * `Record<path, message[]>` for per-field surfacing. Verbatim from `lib/api.ts`.
 */
function mapValidationErrors(errors: unknown): Record<string, string[]> | undefined {
  if (!Array.isArray(errors)) return undefined;
  const out: Record<string, string[]> = {};
  for (const entry of errors) {
    if (!entry || typeof entry !== 'object') continue;
    const e = entry as { path?: string; message?: string };
    const key = e.path && e.path.length > 0 ? e.path : '_form';
    const msg = e.message ?? 'Invalid';
    if (!out[key]) out[key] = [];
    out[key].push(msg);
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

/** API response envelope — matches the backend's ApiResponse<T> shape. */
interface ApiEnvelope<T> {
  data: T;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await apiFetch(`${API_BASE_URL}${ADMIN_PREFIX}${path}`, {
    headers: { 'Content-Type': 'application/json' },
    ...options,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const message =
      typeof errorData.message === 'string' && errorData.message.length > 0
        ? errorData.message
        : response.statusText;
    throw new ApiError(response.status, message, mapValidationErrors(errorData.errors));
  }

  const text = await response.text();
  // 204 No Content returns an empty body.
  if (!text) return undefined as T;
  const envelope: ApiEnvelope<T> = JSON.parse(text);
  return envelope.data;
}

/**
 * The create body the SPA puts on the wire: `advertiserId` and `credentialId`
 * arrive from `<select>` elements as strings; `zCoerceBigInt` on the shared
 * schema handles coercion server-side (conventions §Type-safety).
 */
export type CreatePartnerEndpointWire = Omit<
  CreatePartnerEndpoint,
  'advertiserId' | 'credentialId'
> & {
  advertiserId: string;
  credentialId?: string;
};

/**
 * The update body — partial, no advertiserId (endpoints never move).
 * `credentialId` is a tri-state: an id string points at a credential, `''`
 * (the screen's "None" option) is an explicit clear the shared update schema
 * preserves as `null`, and omitting the field leaves the credential unchanged
 * (F-6/B-06).
 */
export type UpdatePartnerEndpointWire = {
  name?: string;
  transport?: EndpointTransport;
  credentialId?: string;
  variables?: EndpointVariable[];
};

/** All endpoints, optionally narrowed to one advertiser. */
export function getPartnerEndpoints(advertiserId?: string): Promise<PartnerEndpointResponse[]> {
  const query = advertiserId ? `?advertiserId=${encodeURIComponent(advertiserId)}` : '';
  return request<PartnerEndpointResponse[]>(`/partner-endpoints${query}`);
}

export function createPartnerEndpoint(
  body: CreatePartnerEndpointWire
): Promise<PartnerEndpointResponse> {
  return request<PartnerEndpointResponse>('/partner-endpoints', {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export function updatePartnerEndpoint(
  id: string,
  body: UpdatePartnerEndpointWire
): Promise<PartnerEndpointResponse> {
  return request<PartnerEndpointResponse>(`/partner-endpoints/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(body),
  });
}

/** Mark an endpoint `retired` — allowed even when campaigns reference it. */
export function retirePartnerEndpoint(id: string): Promise<PartnerEndpointResponse> {
  return request<PartnerEndpointResponse>(`/partner-endpoints/${id}/retire`, {
    method: 'POST',
  });
}

/** Hard-delete an endpoint — 409 when any campaign references it. */
export function deletePartnerEndpoint(id: string): Promise<void> {
  return request<void>(`/partner-endpoints/${id}`, { method: 'DELETE' });
}

/** Partner credentials filtered by advertiser — for the credential picker. */
export function getPartnerCredentials(
  advertiserId: string
): Promise<PartnerCredentialRowResponse[]> {
  return request<PartnerCredentialRowResponse[]>(
    `/partner-credentials?advertiserId=${encodeURIComponent(advertiserId)}`
  );
}

/**
 * All campaigns for an advertiser — used by the "test with campaign" picker to
 * filter client-side for `crmConfig.endpoint_id` matching the current endpoint.
 * Delegates to the shared typed client (read-only import of `lib/api.ts`).
 */
export function getCampaignsForAdvertiser(advertiserId: string): Promise<CampaignDetailResponse[]> {
  return api.getCampaigns({ advertiserId });
}

/**
 * POST /admin/campaigns/:id/test-runs — admin-mode run from the endpoint
 * screen (ADR-016 §7, epic 031 results screen at `/test-runs/:campaignId`).
 */
export function createTestRun(campaignId: string): Promise<TestRunSummary> {
  return request<TestRunSummary>(`/campaigns/${campaignId}/test-runs`, {
    method: 'POST',
    body: JSON.stringify({ mode: 'admin' }),
  });
}
